import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';

/**
 * CWS-1426: the tenant's switch for membership plan changes (prorated
 * upgrade / downgrade at the lane) and the per-location availability the
 * shop UI gates the Update Membership CTA on.
 */
export interface PlanChangeSettings {
  enabled: boolean;
  /** Empty = every location. */
  location_ids: string[];
  updated_by: string | null;
  updated_at: string | null;
}

export interface PlanChangeLocation {
  id: string;
  branch: string | null;
  timezone: string;
}

export interface PlanChangeSettingsData {
  settings: PlanChangeSettings;
  locations: PlanChangeLocation[];
  /** The platform kill switch (env). Off means the tenant toggle has no effect yet. */
  globally_enabled: boolean;
}

export interface PlanChangeSettingsUpdatePayload {
  enabled: boolean;
  location_ids?: string[];
}

export type PlanChangeAvailabilityReason = 'ok' | 'platform_disabled' | 'location_disabled';

export interface PlanChangeAvailability {
  location_id: string;
  enabled: boolean;
  reason: PlanChangeAvailabilityReason;
}

/**
 * What the settings panel needs from whoever hosts it. The shop service below is
 * the default; the super-admin company page supplies a company-scoped one
 * (CWS-1598), so one panel serves both without knowing which API it talks to.
 */
export interface PlanChangeSettingsSource {
  getSettings(): Observable<ApiResponse<PlanChangeSettingsData>>;
  updateSettings(
    payload: PlanChangeSettingsUpdatePayload
  ): Observable<ApiResponse<PlanChangeSettingsData>>;
}

@Injectable({ providedIn: 'root' })
export class PlanChangeSettingsService implements PlanChangeSettingsSource {
  constructor(private api: ApiService) {}

  getSettings(): Observable<ApiResponse<PlanChangeSettingsData>> {
    return this.api.get<PlanChangeSettingsData>('order/membership-plan-change/settings');
  }

  updateSettings(
    payload: PlanChangeSettingsUpdatePayload
  ): Observable<ApiResponse<PlanChangeSettingsData>> {
    return this.api.put<PlanChangeSettingsData>('order/membership-plan-change/settings', payload);
  }

  getAvailability(locationId: string): Observable<ApiResponse<PlanChangeAvailability>> {
    return this.api.get<PlanChangeAvailability>('order/membership-plan-change/availability', {
      location_id: locationId,
    });
  }
}
